// src/eden/planner/plannerIntakeAssist.js
//
// Deterministic Sherloc Media Planner intake assistant.
//
// Goal: let a user paste rough client/prospect intake notes into Eden from the
// Sherloc Media Planner page and turn them into a structured, editable planner
// draft. This is intentionally a pure, network-free, deterministic parser so it
// works without the heavy /api/eden/ask governance round-trip (which is what was
// failing on the planner handoff) and is fully testable.
//
// Guardrails baked in:
//   - Eden can only DRAFT fields; it never launches, deploys, saves, or spends.
//   - Missing inputs are listed, never hallucinated.
//   - Expanded/inferred values are recorded as assumptions.
//
// The mapping into form state is non-destructive: fields that already hold a
// value are reported as conflicts and left untouched unless the user explicitly
// confirms an overwrite.

export const EDEN_PLANNER_DRAFT_EVENT = "cphere:eden-planner-draft";

export const PLANNER_ROUTE = "/sherloc-media-planner";

// Known draft fields Eden is allowed to return. Anything outside this list is
// ignored when mapping into the form.
export const PLANNER_DRAFT_FIELDS = Object.freeze([
  "clientName",
  "campaignTitle",
  "filmTitle",
  "totalBudget",
  "netMediaBudget",
  "targetMargin",
  "goalType",
  "primaryKpi",
  "additionalNotes",
  "officialWebsiteUrl",
  "imdbUrl",
  "rottenTomatoesUrl",
  "ageMin",
  "ageMax",
  "gender",
  "languageTargeting",
  "interests",
  "customTargeting",
  "geoLocationTargeting",
  "devicePlatformNotes",
  "flightStart",
  "flightEnd",
  "selectedPlatforms",
  "recommendedChannelAllocation",
  "targetMarkets",
  "deadline",
]);

// Draft field -> planner form-state key. A draft field whose form key is absent
// from the current form (e.g. netMediaBudget only exists in superadmin) is
// simply skipped, so one mapping table serves both apps.
/*
 * Draft fields that carry INTERNAL economics. `netMediaBudget` is Cphere's
 * cost and `targetMargin` is Cphere's take; neither may be shown to, drafted
 * for, or mentioned to a client — and neither can be honestly relabelled,
 * because renaming a NET amount "client-billed" would misstate the number.
 *
 * The header comment above once claimed these were "skipped" in Cortex
 * because the form lacks the keys. That is not true: Cortex's planner form
 * carries both keys, and `summarizePlannerDraft` builds its chat line from
 * the DRAFT rather than the form — so Eden could tell a client
 * "Ready to fill: Net Media Budget, Target Margin (%)".
 *
 * This module stays byte-mirrored across both apps, so the scope decision is
 * made by the CALLER (which knows whether it is the client portal) rather
 * than baked in here.
 */
export const INTERNAL_ONLY_DRAFT_FIELDS = Object.freeze(["netMediaBudget", "targetMargin"]);

/**
 * Strip internal-economics fields from a draft before it is applied, shown,
 * summarised, persisted or replayed on a client surface.
 */
export function stripInternalDraftFields(draft = {}) {
  if (!draft || typeof draft !== "object") return draft;
  const next = { ...draft };
  INTERNAL_ONLY_DRAFT_FIELDS.forEach((key) => {
    delete next[key];
  });
  return next;
}

const DRAFT_TO_FORM_FIELD = Object.freeze({
  campaignTitle: { formKey: "campaignName", label: "Campaign / Film Title" },
  totalBudget: { formKey: "budget", label: "Budget (Total)" },
  netMediaBudget: { formKey: "netMediaBudget", label: "Net Media Budget" },
  targetMargin: { formKey: "targetMarginPercent", label: "Target Margin (%)" },
  goalType: { formKey: "goalType", label: "Goal Type" },
  primaryKpi: { formKey: "primaryKPI", label: "Primary KPI" },
  additionalNotes: { formKey: "notes", label: "Additional Notes" },
  officialWebsiteUrl: { formKey: "destinationUrl", label: "Official Website URL" },
  imdbUrl: { formKey: "imdbUrl", label: "IMDb URL" },
  rottenTomatoesUrl: { formKey: "rottenTomatoesUrl", label: "Rotten Tomatoes URL" },
  ageMin: { formKey: "ageMin", label: "Age Min" },
  ageMax: { formKey: "ageMax", label: "Age Max" },
  gender: { formKey: "gender", label: "Gender" },
  languageTargeting: { formKey: "languageTargeting", label: "Language Targeting" },
  interests: { formKey: "interests", label: "Interests" },
  customTargeting: { formKey: "customTargeting", label: "Custom Targeting" },
  geoLocationTargeting: { formKey: "geoTargetingNotes", label: "Geo / Location Targeting" },
  devicePlatformNotes: { formKey: "deviceTargetingNotes", label: "Device / Platform Notes" },
  flightStart: { formKey: "flightStart", label: "Flight Start" },
  flightEnd: { formKey: "flightEnd", label: "Flight End" },
  selectedPlatforms: { formKey: "platforms", label: "Channels / Platforms" },
});

// Channel phrase -> planner platform key(s).
const CHANNEL_KEYWORDS = [
  { re: /\bmeta\b|facebook\s*\+\s*instagram|facebook\s*and\s*instagram/i, keys: ["facebook", "instagram"] },
  { re: /\bfacebook\b/i, keys: ["facebook"] },
  { re: /\binstagram\b|\big\b/i, keys: ["instagram"] },
  { re: /\byoutube\b|\byt\b/i, keys: ["youtube"] },
  { re: /\bctv\b|connected\s*tv|streaming\s*tv/i, keys: ["ctv"] },
  { re: /\btiktok\b/i, keys: ["tiktok"] },
  { re: /\bdisplay\b|retargeting|programmatic\s*display/i, keys: ["display"] },
  { re: /\bgoogle\s*search\b|\bsearch\b|\bsem\b/i, keys: ["google_search"] },
  { re: /\baudio\b|podcast|spotify/i, keys: ["audio"] },
  { re: /\bdooh\b|out\s*of\s*home|\booh\b/i, keys: ["dooh"] },
  { re: /\blinkedin\b/i, keys: ["linkedin"] },
  { re: /\breddit\b/i, keys: ["reddit"] },
  { re: /\bx\b(?!\w)|twitter/i, keys: ["x"] },
];

function str(value) {
  return value === undefined || value === null ? "" : String(value);
}

function trimmed(value) {
  return str(value).trim();
}

export function isPlannerRoute(path) {
  const clean = trimmed(path).toLowerCase().replace(/\/+$/, "");
  return clean === PLANNER_ROUTE || clean.endsWith(PLANNER_ROUTE);
}

// Parse a "$125,000", "$125k", "125,000", or "125k" style figure to a number.
function parseMoney(token) {
  const cleaned = trimmed(token).replace(/[,$\s]/g, "").toLowerCase();
  const match = cleaned.match(/^(\d+(?:\.\d+)?)(k|m)?$/);
  if (!match) return null;
  let value = Number(match[1]);
  if (!Number.isFinite(value)) return null;
  if (match[2] === "k") value *= 1000;
  if (match[2] === "m") value *= 1000000;
  return Math.round(value);
}

// Collect dollar figures with light context (tier label) so we can pick the
// high-tier budget rather than the first number we see.
function collectBudgets(text) {
  const out = [];
  const re = /\$\s?(\d[\d,]*(?:\.\d+)?\s?[km]?)/gi;
  let m;
  while ((m = re.exec(text)) !== null) {
    const value = parseMoney(m[1]);
    if (value == null) continue;
    const around = text.slice(Math.max(0, m.index - 40), m.index + 40).toLowerCase();
    out.push({ value, context: around, index: m.index });
  }
  return out;
}

function detectChannels(text) {
  const keys = [];
  for (const { re, keys: channelKeys } of CHANNEL_KEYWORDS) {
    if (re.test(text)) {
      for (const key of channelKeys) if (!keys.includes(key)) keys.push(key);
    }
  }
  return keys;
}

// Heuristic: does this text look like client intake worth structuring, vs a
// normal analytics question? Requires a budget figure plus at least one of
// channel/market/film signals, OR several distinct planner signals.
export function looksLikePlannerIntake(text) {
  const value = trimmed(text);
  if (value.length < 40) return false;

  const hasBudget = /\$\s?\d/.test(value) || /\bbudget\b/i.test(value);
  const hasChannels = detectChannels(value).length > 0;
  const hasFilm = /\b(film|movie|documentary|title|release|theatrical)\b/i.test(value);
  const hasMarkets = /\b(market|markets|dma|geo|cities|corridor|nationwide|region)\b/i.test(value);
  const hasAllocation = /\d{1,3}\s?%/.test(value) || /\ballocation\b/i.test(value);

  const signals = [hasBudget, hasChannels, hasFilm, hasMarkets, hasAllocation].filter(Boolean).length;

  if (hasBudget && (hasChannels || hasMarkets || hasFilm || hasAllocation)) return true;
  return signals >= 3;
}

function firstMatch(text, regexes) {
  for (const re of regexes) {
    const m = str(text).match(re);
    if (m && trimmed(m[1])) return trimmed(m[1]);
  }
  return "";
}

const PLATFORM_KEY_TO_LABEL = {
  facebook: "Facebook",
  instagram: "Instagram",
  youtube: "YouTube",
  ctv: "CTV",
  tiktok: "TikTok",
  display: "Display/Retargeting",
  google_search: "Google Search",
  audio: "Audio",
  dooh: "DOOH",
  linkedin: "LinkedIn",
  reddit: "Reddit",
  x: "X",
};

// Canonical channel allocation extraction: find "<Channel> NN% ($amount)" pairs
// anywhere in the text (works for both bullet lists and one-line intake).
const ALLOCATION_CHANNELS = [
  { label: "Meta", re: /\bmeta\b/i, covers: ["facebook", "instagram"] },
  { label: "Facebook", re: /\bfacebook\b/i, covers: ["facebook"] },
  { label: "Instagram", re: /\binstagram\b/i, covers: ["instagram"] },
  { label: "YouTube", re: /\byoutube\b/i, covers: [] },
  { label: "CTV", re: /\bctv\b/i, covers: [] },
  { label: "TikTok", re: /\btiktok\b/i, covers: [] },
  { label: "Display/Retargeting", re: /\bdisplay\b|retargeting/i, covers: [] },
  { label: "Audio", re: /\baudio\b/i, covers: [] },
  { label: "DOOH", re: /\bdooh\b/i, covers: [] },
];

function extractChannelAllocations(text) {
  const value = str(text);
  const rows = [];
  const usedMeta = /\bmeta\b/i.test(value);
  for (const { label, re } of ALLOCATION_CHANNELS) {
    // When "Meta" is named, skip standalone Facebook/Instagram allocation rows.
    if (usedMeta && (label === "Facebook" || label === "Instagram")) continue;
    const m = re.exec(value);
    if (!m) continue;
    const after = value.slice(m.index, m.index + 52);
    const pct = after.match(/(\d{1,3})\s?%/);
    const amt = after.match(/\$\s?\d[\d,]*(?:\.\d+)?\s?[km]?/i);
    if (!pct && !amt) continue;
    let entry = label;
    if (pct) entry += ` ${pct[1]}%`;
    if (amt) entry += ` (${amt[0].replace(/\s/g, "")})`;
    rows.push(entry);
  }
  return rows;
}

// Tornado Alley corridor + common extra markets, in canonical display order.
const TORNADO_ALLEY_CITIES = [
  "Dallas–Fort Worth",
  "Houston",
  "Kansas City",
  "Oklahoma City",
  "Tulsa",
  "Wichita",
  "Omaha",
  "Indianapolis",
  "Twin Cities",
  "Denver",
  "Chicago",
];

const GEO_CITY_MATCHERS = [
  ["Dallas–Fort Worth", /dallas[-–\s]*fort\s*worth|\bdallas\b|fort worth|\bdfw\b/i],
  ["Houston", /\bhouston\b/i],
  ["Kansas City", /kansas city/i],
  ["Oklahoma City", /oklahoma city|\bokc\b/i],
  ["Tulsa", /\btulsa\b/i],
  ["Wichita", /\bwichita\b/i],
  ["Omaha", /\bomaha\b/i],
  ["Indianapolis", /indianapolis|\bindy\b/i],
  ["Twin Cities", /twin cities|minneapolis|st\.?\s*paul/i],
  ["Denver", /\bdenver\b/i],
  ["Chicago", /\bchicago\b/i],
  // City words match case-insensitively; bare abbreviations only as uppercase
  // tokens so we don't match stray lowercase "ny"/"la".
  ["New York", (v) => /new york/i.test(v) || /\bNYC\b|\bNY\b/.test(v)],
  ["Los Angeles", (v) => /los angeles/i.test(v) || /\bLA\b/.test(v)],
];

// Build an expanded, deduped, canonically-ordered geo string. Returns
// { geo, expandedFromTornadoAlley }.
function expandGeoTargeting(text) {
  const value = str(text);
  const tornadoAlley = /tornado alley/i.test(value);
  const present = new Set();
  for (const [name, matcher] of GEO_CITY_MATCHERS) {
    const matched = typeof matcher === "function" ? matcher(value) : matcher.test(value);
    if (matched) present.add(name);
  }
  if (tornadoAlley) {
    for (const city of TORNADO_ALLEY_CITIES) present.add(city);
  }
  if (!present.size) return { geo: "", expandedFromTornadoAlley: false };
  const ordered = GEO_CITY_MATCHERS.map(([name]) => name).filter((name) => present.has(name));
  return { geo: ordered.join(", "), expandedFromTornadoAlley: tornadoAlley };
}

/**
 * Convert freeform intake text into a structured planner draft.
 * @returns {{ draft: object, assumptions: string[], missingInformation: string[], confidence: "low"|"medium"|"high" }}
 */
export function parsePlannerIntake(rawText) {
  const text = str(rawText);
  const draft = {};
  const assumptions = [];
  const missingInformation = [];

  // --- Title -------------------------------------------------------------
  const filmTitle = firstMatch(text, [
    /\bfilm is\s+([^,.\n]+?)(?:,|\.|\bis a\b|\breleasing\b|\n|$)/i,
    /\bmovie is\s+([^,.\n]+?)(?:,|\.|\n|$)/i,
    /\btitle[:\s]+([^,.\n]+)/i,
    /\bfor\s+([A-Z][\w&'’ -]+?)\s+(?:campaign|film|movie|documentary)\b/,
    // "Never Stop Chasing, a storm-chasing documentary"
    /([A-Z][\w'’ -]+?),\s*a\s+[^,.\n]*\b(?:documentary|film|movie|series)\b/i,
    // Leading title immediately followed by a genre descriptor:
    // "Never Stop Chasing horror doc, ..." / "Never Stop Chasing storm-chasing documentary"
    /^([A-Z][\w'’-]*(?:\s+[A-Z][\w'’-]*){0,6}?)\s+(?:storm[- ]?chasing|horror|thriller|documentary|doc\b|comedy|drama|action|family)/,
    // Leading title before the first comma (one-line intake), excluding greetings.
    /^(?!(?:hi|hello|hey|dear|thanks|thank you|team)\b)([A-Z][\w'’ -]+?),/i,
  ]);
  if (filmTitle) {
    draft.filmTitle = filmTitle;
    draft.campaignTitle = filmTitle;
  } else {
    missingInformation.push("Campaign / film title");
  }

  // --- Client / advertiser name -----------------------------------------
  let clientName = firstMatch(text, [
    /\bfor\s+([A-Z][\w&'’.-]*(?:\s+[A-Z][\w&'’.-]*)*?)(?=\s+(?:and|need|by|if|on|,|\.|\n))/,
  ]);
  if (!clientName) {
    const firstLine = str(text).split(/\r?\n/)[0] || "";
    const segments = firstLine.split(",").map((s) => s.trim());
    for (let i = 1; i < segments.length; i += 1) {
      const seg = segments[i];
      if (!seg || /[$%\d]/.test(seg)) continue;
      if (detectChannels(seg).length) continue;
      if (/tornado|alley|\bNY\b|\bLA\b|new york|los angeles|eod|monday|tuesday|wednesday|thursday|friday|budget|high|medium|\blow\b|tier/i.test(seg)) {
        continue;
      }
      if (/^[A-Z][\w&'’. -]+$/.test(seg)) {
        clientName = seg;
        break;
      }
    }
  }
  if (clientName && (!draft.filmTitle || clientName.toLowerCase() !== draft.filmTitle.toLowerCase())) {
    draft.clientName = clientName;
  }

  // --- Budgets -----------------------------------------------------------
  const budgets = collectBudgets(text);
  if (budgets.length) {
    const highTier =
      budgets.find((b) => /high[- ]?tier|top[- ]?tier|primary|main/.test(b.context)) || null;
    const chosen = highTier || budgets.reduce((a, b) => (b.value > a.value ? b : a), budgets[0]);
    draft.totalBudget = String(chosen.value);
    if (!highTier && budgets.length > 1) {
      assumptions.push(
        `Used the largest stated figure ($${chosen.value.toLocaleString()}) as the total/high-tier budget; confirm which tier to build from.`
      );
    }
  } else {
    missingInformation.push("Total budget");
  }

  // --- Channel selection + allocation -----------------------------------
  const channelKeys = detectChannels(text);
  const allocationRows = extractChannelAllocations(text);
  const filmContext =
    /tornado alley|storm|chasing|documentary|\bdoc\b|theatrical|film|movie/i.test(text) ||
    Boolean(draft.filmTitle);
  if (channelKeys.length) {
    draft.selectedPlatforms = channelKeys;
    if (allocationRows.length) {
      draft.recommendedChannelAllocation = allocationRows.join(", ");
    }
    const deviceParts = [];
    if (allocationRows.length) {
      deviceParts.push(`${allocationRows.join(", ")}.`);
    } else {
      deviceParts.push(
        `${channelKeys.map((k) => PLATFORM_KEY_TO_LABEL[k] || k).join(", ")}.`
      );
    }
    deviceParts.push("Prioritize mobile and video-first placements.");
    if (channelKeys.includes("ctv")) {
      deviceParts.push("Use CTV for premium theatrical reach.");
    }
    draft.devicePlatformNotes = deviceParts.join(" ");
    assumptions.push(
      "Device/placement guidance (mobile/video-first, CTV for premium reach) is a recommendation; adjust to your delivery plan."
    );
  } else {
    missingInformation.push("Desired channels / platform mix");
  }

  // --- Target markets / geo (with Tornado Alley corridor expansion) ------
  const explicitMarkets = firstMatch(text, [
    /target markets?[:\s]+([^\n]+)/i,
    /\bmarkets?[:\s]+([^\n]+)/i,
  ]);
  const { geo, expandedFromTornadoAlley } = expandGeoTargeting(text);
  if (geo) {
    draft.geoLocationTargeting = geo;
    draft.targetMarkets = explicitMarkets || geo;
    if (expandedFromTornadoAlley) {
      assumptions.push(
        'Expanded "Tornado Alley" to its core DMAs (Dallas–Fort Worth, Houston, Kansas City, Oklahoma City, Tulsa, Wichita, Omaha, Indianapolis, Twin Cities, Denver, Chicago); confirm the exact market list.'
      );
    }
  } else if (explicitMarkets) {
    draft.geoLocationTargeting = explicitMarkets;
    draft.targetMarkets = explicitMarkets;
  } else {
    missingInformation.push("Target markets / geography");
  }

  // --- Deadline ----------------------------------------------------------
  const deadline = firstMatch(text, [
    /\bby\s+([A-Z][a-z]+day(?:\s+[A-Z]{2,3})?(?:\s+EOD)?)/,
    /\b([A-Z][a-z]+day\s+EOD)\b/,
    /\bdue\s+([^,.\n]+)/i,
    /\bdeadline[:\s]+([^,.\n]+)/i,
  ]);
  if (deadline) {
    draft.deadline = deadline;
  }

  // --- Release window (informs notes, not a hard flight date) -----------
  const release = firstMatch(text, [
    /releasing\s+in\s+([^,.\n]+)/i,
    /\brelease[:\s]+([^,.\n]+)/i,
  ]);

  // --- Interests expansion (film/storm context), marked as assumptions ---
  const stormContext = /tornado|storm|chasing|severe weather/i.test(text);
  const documentaryContext = /documentary|\bdoc\b/i.test(text) || stormContext;
  if (filmContext) {
    const interestSeed = [];
    if (stormContext) {
      interestSeed.push("Storm chasing", "Severe weather", "Tornadoes", "Weather enthusiasts");
    }
    if (documentaryContext) {
      interestSeed.push("Documentary films", "Theatrical documentaries");
    }
    interestSeed.push("Survival and nature content");
    if (channelKeys.includes("youtube") && stormContext) {
      interestSeed.push("YouTube weather audiences");
    }
    if (interestSeed.length) {
      draft.interests = interestSeed.join(", ");
      assumptions.push(
        `Suggested interests (${interestSeed.join(", ")}) are inferred from the title/genre and geography; refine before launch.`
      );
    }

    // Custom targeting expansion (standard film retargeting ladder).
    draft.customTargeting =
      "Retarget website visitors, trailer viewers, video engagers, social engagers, CRM audiences if available, lookalikes from known social/video audiences.";
    assumptions.push(
      "Custom targeting is a suggested retargeting/lookalike ladder; connect the actual audiences/pixels before launch."
    );
  }

  // --- Primary KPI + goal (awareness-led theatrical) ---------------------
  const awarenessSignals =
    /projected impressions|reach by channel|impressions and reach|awareness|reach/i.test(text);
  if (awarenessSignals || filmContext) {
    draft.goalType = "awareness";
    draft.primaryKpi =
      "Projected impressions, reach, video completion rate, CTR, and efficient CPM by channel";
    assumptions.push(
      'Inferred goal "Reach / Awareness" with reach/impressions, VCR, CTR, and CPM KPIs; adjust if the goal is ticket sales or conversions.'
    );
  } else {
    missingInformation.push("Primary KPI / performance goal");
  }

  // --- Additional notes: restate the client's explicit instructions ------
  const noteParts = [];
  if (release) noteParts.push(`Release: ${release}.`);
  const scaleTiers = collectBudgets(text)
    .filter((b) => /medium|low|scale|smaller/.test(b.context) || b.value < (Number(draft.totalBudget) || Infinity))
    .map((b) => b.value)
    .filter((v, i, arr) => arr.indexOf(v) === i && String(v) !== draft.totalBudget)
    .sort((a, b) => b - a)
    .map((v) => `$${v.toLocaleString()}`);
  if (draft.totalBudget) {
    const scalePart = scaleTiers.length ? ` scale to ${scaleTiers.join(" and ")};` : "";
    noteParts.push(
      `Build the digital recommendation from the $${Number(draft.totalBudget).toLocaleString()} high-tier budget;${scalePart} preserve the requested channel percentages where deliverable.`
    );
  }
  if (/deliverability/i.test(text)) {
    noteParts.push("Flag any deliverability issues with the requested channel mix.");
  }
  if (/projected impressions|reach by channel/i.test(text)) {
    noteParts.push("Deliverable: projected impressions and reach by channel.");
  }
  if (noteParts.length) {
    draft.additionalNotes = noteParts.join(" ");
  }

  // --- Fields we deliberately do NOT invent ------------------------------
  if (!/\bage\b|\b\d{2}\s?[-–]\s?\d{2}\b/i.test(text)) missingInformation.push("Audience age range");
  if (!/\bgender\b|\bmale\b|\bfemale\b/i.test(text)) missingInformation.push("Gender targeting");
  if (!/\blanguage\b|english|spanish/i.test(text)) missingInformation.push("Language targeting");
  if (!/https?:\/\//i.test(text)) missingInformation.push("Official / IMDb / Rotten Tomatoes URLs");
  if (!/margin/i.test(text)) missingInformation.push("Target margin");
  if (!/flight|start date|end date|in-?market dates|run date/i.test(text)) {
    missingInformation.push("Flight start/end dates");
  }
  if (draft.clientName) {
    assumptions.push(
      `Client appears to be ${draft.clientName} — select the matching advertiser in the dropdown so the planner is correctly scoped.`
    );
  }

  const filledCount = PLANNER_DRAFT_FIELDS.filter((k) => {
    const v = draft[k];
    return Array.isArray(v) ? v.length > 0 : trimmed(v).length > 0;
  }).length;
  const confidence = filledCount >= 8 ? "high" : filledCount >= 4 ? "medium" : "low";

  return { draft, assumptions, missingInformation, confidence };
}

function platformsEqual(a, b) {
  const aa = Array.isArray(a) ? [...a].sort() : [];
  const bb = Array.isArray(b) ? [...b].sort() : [];
  return aa.length === bb.length && aa.every((v, i) => v === bb[i]);
}

/**
 * Map a structured draft into planner form state without destroying existing
 * manual input.
 *
 * @param {object} draft  - structured draft (from parsePlannerIntake().draft)
 * @param {object} currentForm - current planner form state
 * @param {object} [options]
 * @param {string[]} [options.overwriteFields] - draft field names the user
 *        explicitly confirmed should overwrite an existing value.
 * @returns {{ nextForm: object, applied: Array, conflicts: Array, unchanged: Array }}
 */
export function mapPlannerDraftToForm(draft = {}, currentForm = {}, options = {}) {
  /*
   * `clientScoped` is the portal telling this module who is watching. Cortex
   * passes it; Quantum does not, and keeps the full internal draft.
   */
  if (options.clientScoped) draft = stripInternalDraftFields(draft);
  const overwrite = new Set(options.overwriteFields || []);
  const nextForm = { ...currentForm };
  const applied = [];
  const conflicts = [];
  const unchanged = [];

  for (const [draftKey, mapping] of Object.entries(DRAFT_TO_FORM_FIELD)) {
    const { formKey, label } = mapping;
    // Only touch fields the current form actually exposes.
    if (!(formKey in currentForm)) continue;

    let incoming = draft[draftKey];
    if (formKey === "platforms") {
      incoming = Array.isArray(incoming) ? incoming.filter(Boolean) : [];
      if (!incoming.length) continue;
    } else {
      incoming = trimmed(incoming);
      if (!incoming) continue; // never blank-out / hallucinate
    }

    const currentValue = currentForm[formKey];
    const currentIsEmpty =
      formKey === "platforms"
        ? !(Array.isArray(currentValue) && currentValue.length)
        : !trimmed(currentValue) || trimmed(currentValue) === "all"; // "all" gender default counts as empty

    const isSame =
      formKey === "platforms"
        ? platformsEqual(currentValue, incoming)
        : trimmed(currentValue) === trimmed(incoming);

    if (isSame) {
      unchanged.push({ field: draftKey, formKey, label });
      continue;
    }

    if (currentIsEmpty || overwrite.has(draftKey)) {
      nextForm[formKey] = incoming;
      applied.push({ field: draftKey, formKey, label, value: incoming });
    } else {
      conflicts.push({
        field: draftKey,
        formKey,
        label,
        currentValue,
        suggestedValue: incoming,
      });
    }
  }

  return { nextForm, applied, conflicts, unchanged };
}

/** Calm, non-technical chat summary of what Eden drafted. */
export function summarizePlannerDraft(result, options = {}) {
  const { assumptions = [], missingInformation = [], confidence = "low" } = result || {};
  /*
   * The chat line is built from the DRAFT, not the form, so a client-scoped
   * caller must strip the internal fields here too — otherwise Eden names
   * them even on a portal that never renders their inputs.
   */
  const draft = options.clientScoped
    ? stripInternalDraftFields(result?.draft || {})
    : result?.draft || {};
  const filledLabels = Object.entries(DRAFT_TO_FORM_FIELD)
    .filter(([draftKey, m]) =>
      m.formKey === "platforms"
        ? Array.isArray(draft[draftKey]) && draft[draftKey].length
        : trimmed(draft[draftKey])
    )
    .map(([, m]) => m.label);

  const lines = [];
  lines.push("I structured your intake into a planner draft. Nothing is saved or launched — review and apply when you're ready.");
  if (draft.clientName) {
    lines.push(`Client: ${draft.clientName}.`);
  }
  if (filledLabels.length) {
    lines.push(`Ready to fill: ${filledLabels.join(", ")}.`);
  }
  if (assumptions.length) {
    lines.push(`Assumptions to confirm: ${assumptions.join(" ")}`);
  }
  if (missingInformation.length) {
    lines.push(`Still needs your input: ${missingInformation.join(", ")}.`);
  }
  lines.push(`Confidence: ${confidence}. Use “Apply to Planner” to populate the fields, then edit anything before generating a strategy.`);
  return lines.join("\n\n");
}

export function buildPlannerDraftDetail(rawText, options = {}) {
  const parsed = parsePlannerIntake(rawText);
  /*
   * On a client portal the internal fields are removed from the RESULT as
   * well as the summary. The detail is dispatched as an event, held in
   * component state and replayed on rehydration, so leaving them on the
   * object would keep Cphere's cost and take reachable long after the chat
   * line that named them was gone.
   */
  const result = options.clientScoped
    ? { ...parsed, draft: stripInternalDraftFields(parsed?.draft || {}) }
    : parsed;
  return {
    source: "eden_planner_assist",
    result,
    summary: summarizePlannerDraft(result, options),
  };
}

export function dispatchPlannerDraft(
  rawText,
  targetWindow = typeof window !== "undefined" ? window : null,
  options = {}
) {
  if (!targetWindow?.dispatchEvent) return null;
  const detail = buildPlannerDraftDetail(rawText, options);
  targetWindow.dispatchEvent(new CustomEvent(EDEN_PLANNER_DRAFT_EVENT, { detail }));
  return detail;
}
